// F1 App-Shell (TES-948): AppLayout verdrahtet die extrahierten Shell-Bausteine.
// Desktop = SideNav + TopAppBar. Mobile (< md) = MobileTopBar + BottomNav + NavDrawer.
// MFA/Registration-Gate, OfflineBanner, GuideTour/DashboardTour, DevModeBanner,
// LayoutDebugLegend bleiben. Keine Route-/App.tsx-Aenderung noetig.
import { ReactNode, Suspense, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { LayoutDebugLegend } from "@/components/admin/LayoutDebugHelper";
import { DevModeBanner } from "@/components/dev/DevModeBanner";
import { OfflineBanner } from "./OfflineBanner";
import { GuideTour } from "@/components/tour/GuideTour";
import { DashboardTour } from "@/components/tour/DashboardTour";
import { AssistantPanel } from "@/components/chat/AssistantPanel";
import { HelpChatButton } from "@/components/help/HelpChatButton";
import { SideNav, TopAppBar, MobileTopBar, BottomNav, NavDrawer, useShellModel } from "./shell";
import { RouteSuspenseFallback } from "./RouteSuspenseFallback";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const { mfaRequired, mfaEnrollmentRequired, registrationPending } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const onMobileNavClick = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const model = useShellModel();

  // MFA / Registration Gate: render children fullscreen without sidebar/header
  if (mfaRequired || mfaEnrollmentRequired || registrationPending) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
      >
        {model.t("common.skipToMainContent")}
      </a>
      {/* TES-1090: Banner sitzt IM Layout-Flow über der Shell — Shell (SideNav/
          TopAppBar/Content) beginnt unterhalb der Banner-Höhe, keine Überlappung. */}
      <DevModeBanner />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop sidebar */}
        <SideNav
          model={model}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
        />

        {/* Mobile slide-in drawer (full role navigation) */}
        <NavDrawer
          model={model}
          open={mobileMenuOpen}
          onOpenChange={setMobileMenuOpen}
          onNavigate={onMobileNavClick}
        />

        {/* Main content */}
        <main
          id="main-content"
          tabIndex={-1}
          aria-label={model.t("common.mainContent")}
          className="flex-1 overflow-auto bg-background flex flex-col"
        >
          <OfflineBanner />

          {/* Desktop header */}
          <TopAppBar />

          {/* Mobile header */}
          <MobileTopBar model={model} onOpenMenu={() => setMobileMenuOpen(true)} />

          <LayoutDebugLegend />

          {/* Content — extra bottom padding on mobile so BottomNav doesn't cover it */}
          {/* Suspense INNERHALB der Shell: beim Nachladen eines Lazy-Route-Chunks bleiben
              SideNav/TopAppBar stehen (kein Layout-Remount/State-Verlust). (TES-1653) */}
          <div className="flex-1 pb-16 md:pb-0">
            <Suspense fallback={<RouteSuspenseFallback />}>{children}</Suspense>
          </div>
        </main>

        {/* AI assistant — desktop docked rail/panel (right edge of the shell);
            the mobile FAB + bottom sheet render via fixed positioning inside.
            Gated on feature flag + active provider (renders nothing otherwise). */}
        <AssistantPanel />

        {/* Mobile bottom navigation */}
        <BottomNav model={model} />
      </div>

      {/* Floating AI help button (gated on its own flag + provider) */}
      <HelpChatButton />

      <GuideTour />
      <DashboardTour />
    </div>
  );
}
